import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const healthColor = (h) => {
  if (h >= 80) return { text: "text-emerald-600", bg: "bg-emerald-500", label: "Excellent" };
  if (h >= 60) return { text: "text-yellow-600", bg: "bg-yellow-500", label: "Good" };
  if (h >= 40) return { text: "text-orange-600", bg: "bg-orange-500", label: "Fair" };
  return { text: "text-red-600", bg: "bg-red-500", label: "Needs Attention" };
};

const healthColorAr = (h) => {
  if (h >= 80) return "ممتاز";
  if (h >= 60) return "جيد";
  if (h >= 40) return "مقبول";
  return "يحتاج عناية";
};

const ProgressBar = ({ value, color = "bg-emerald-500" }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  return (
    <div ref={ref} className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
      <motion.div
        className={`h-full rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={inView ? { width: `${Math.min(value, 100)}%` } : { width: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      />
    </div>
  );
};

const FieldCard = ({ field, isArabic }) => {
  const hc = healthColor(field.cropHealth);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-lg transition-shadow group">
      {/* Header */}
      <div className={`flex items-center justify-between mb-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        <h4 className="text-sm font-bold text-slate-900 truncate">{field.title}</h4>
        {field.area && (
          <span className="text-[10px] font-bold text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 flex-shrink-0">
            {field.area}
          </span>
        )}
      </div>

      {/* Current crop */}
      {field.currentCrop && (
        <div className={`flex items-center gap-2 mb-3 ${isArabic ? "flex-row-reverse" : ""}`}>
          <span className="text-sm">🌱</span>
          <span className="text-sm text-slate-700 font-medium">
            {field.currentCrop}
            {field.variety ? ` (${field.variety})` : ""}
          </span>
        </div>
      )}

      {/* Crop health */}
      {field.cropHealth != null && (
        <div className="mb-3">
          <div className={`flex items-center justify-between mb-1.5 ${isArabic ? "flex-row-reverse" : ""}`}>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {isArabic ? "صحة المحصول" : "Crop Health"}
            </span>
            <span className={`text-xs font-bold ${hc.text}`}>
              {field.cropHealth}% — {isArabic ? healthColorAr(field.cropHealth) : hc.label}
            </span>
          </div>
          <ProgressBar value={field.cropHealth} color={hc.bg} />
        </div>
      )}

      {/* Season progress */}
      {field.seasonProgress != null && (
        <div>
          <div className={`flex items-center justify-between mb-1.5 ${isArabic ? "flex-row-reverse" : ""}`}>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {isArabic ? "تقدم الموسم" : "Season Progress"}
            </span>
            <span className="text-xs font-bold text-slate-600">{Math.round(field.seasonProgress)}%</span>
          </div>
          <ProgressBar value={field.seasonProgress} color="bg-blue-500" />
        </div>
      )}

      {/* No crop fallback */}
      {!field.currentCrop && field.cropHealth == null && (
        <p className="text-xs text-slate-400 italic mb-0">{isArabic ? "لا يوجد محصول نشط" : "No active crop"}</p>
      )}
    </div>
  );
};

const FieldsGrid = ({ fields, isArabic }) => {
  if (!fields || fields.length === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
      <div className={`flex items-center gap-2 mb-5 ${isArabic ? "flex-row-reverse" : ""}`}>
        <span className="text-lg">🌾</span>
        <h3 className="text-base font-bold text-slate-900 mb-0">
          {isArabic ? "نظرة على الحقول" : "Fields Overview"}
        </h3>
        <span className="text-xs text-slate-400 font-medium">({fields.length})</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map((field) => (
          <FieldCard key={field.id} field={field} isArabic={isArabic} />
        ))}
      </div>
    </div>
  );
};

export default FieldsGrid;
